import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDragLayer } from 'react-dnd';
import {
  DraggableType,
  getEventTimeInSeconds,
  ICalculateNewEvent,
  IGetEventPosition,
} from './utils';
import { IEvent } from '~/Models/DTO/Event.model';
import { debounce } from 'lodash-es';
import {
  ICalendarOffset,
  IEventPosition,
  IEventTimeInSeconds,
} from '~/Screens/Calendar/interfaces';

interface IUseCalendarDragLayer {
  offset: ICalendarOffset;
  calendarType: DraggableType;
  getEventPosition: IGetEventPosition;
  calculateNewEvent?: (props: ICalculateNewEvent) => IEvent;
}

export const useCalendarDragLayer = ({
  offset,
  calendarType,
  getEventPosition,
  calculateNewEvent,
}: IUseCalendarDragLayer) => {
  const { itemType, isDragging, item, differenceFromInitialOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
    differenceFromInitialOffset: monitor.getDifferenceFromInitialOffset(),
  }));
  const [eventTime, setEventTime] = useState<IEventTimeInSeconds | null>(null);
  const [newEvent, setNewEvent] = useState<IEvent | null>(null);
  const eventPosition = useMemo((): null | IEventPosition => {
    if (!item || !differenceFromInitialOffset) {
      return null;
    }
    return getEventPosition({ item, differenceFromInitialOffset });
  }, [item, differenceFromInitialOffset, getEventPosition]);
  const updateDraggableEvent = useCallback(
    debounce((props: ICalculateNewEvent) => {
      if (!calculateNewEvent) {
        return;
      }
      const newEvent = calculateNewEvent(props);
      setNewEvent(newEvent);
    }, 70),
    [setNewEvent, calculateNewEvent],
  );
  useEffect(() => {
    if (item?.event) {
      setNewEvent(item.event);
      setEventTime(getEventTimeInSeconds(item.event));
    }
  }, [item?.event]);

  useEffect(() => {
    if (!calculateNewEvent || !item || itemType !== calendarType || !eventPosition || !eventTime) {
      return;
    }
    updateDraggableEvent({
      time: eventTime,
      event: item.event,
      position: eventPosition,
      container: offset,
    });
  }, [itemType, calendarType, eventPosition, offset, eventTime, calculateNewEvent]);

  return { eventPosition, isDragging, newEvent, itemType };
};
